/**
 * C-42 «Входящий звонок» (изменение v1.14).
 *
 * По образцу входящего вызова FaceTime и Telegram (Mobbin): имя и аватар по центру, «Отклонить»
 * слева и «Ответить» справа. Экран поверх любого другого, звонит ровно одна видимая вкладка
 * (остальные вкладки показывают вызов молча). Если настройка требует ретранслятор, а сервер без
 * него, согласие на прямое соединение спрашивается здесь же: отдельный диалог оказался бы под экраном.
 */
import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'

import type { Call } from '@entities/call/model'
import { callManager } from '@features/calls/manager'
import { relayRequired } from '@features/calls/relay'
import { contactsRepo } from '@shared/db'
import { useModalLayer } from '@shared/lib/modal-layer'
import { playTone, unlockTones } from '@shared/rtc/tones'
import { Avatar, Icon, cx } from '@shared/ui'

import { useAppStore } from '../../model/app-store'
import { useCallPeer } from './use-call-peer'
import styles from './call.module.css'

const RINGTONE_LOCK = 'dvizh-ringtone'

/**
 * Мелодия входящего звучит ровно в одной видимой вкладке: её выбирает Web Lock. Вкладка-лидер тут
 * не годится — она может быть скрыта, а видимая вкладка тогда молчала бы (системного показа при
 * видимой вкладке service worker тоже не делает). Без Web Locks звонит любая видимая вкладка.
 */
function useRingtone(): void {
  useEffect(() => {
    let stop: (() => void) | null = null
    let release: (() => void) | null = null
    let requesting = false
    let disposed = false
    const locks = globalThis.navigator?.locks
    const visible = () => typeof document !== 'undefined' && document.visibilityState === 'visible'
    const silence = () => {
      stop?.()
      stop = null
      release?.()
      release = null
    }
    const sync = () => {
      if (!visible()) {
        silence()
        return
      }
      if (stop) return
      if (!locks?.request) {
        stop = playTone('ringtone')
        return
      }
      if (requesting) return
      requesting = true
      void locks
        .request(RINGTONE_LOCK, { ifAvailable: true }, (lock) => {
          requesting = false
          if (!lock || disposed || !visible() || stop) return undefined
          stop = playTone('ringtone')
          return new Promise<void>((resolve) => {
            release = resolve
          })
        })
        .catch(() => {
          requesting = false
        })
    }
    sync()
    document.addEventListener('visibilitychange', sync)
    const timer = setInterval(sync, 2_000)
    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', sync)
      clearInterval(timer)
      silence()
    }
  }, [])
}

export function IncomingCallScreen({ call }: { call: Call }) {
  // Экран нельзя закрыть «назад»: ответить или отклонить — явное действие.
  const { ref, layerRef } = useModalLayer(true, () => undefined, { history: false, escape: false })
  const peer = useCallPeer(call.conversation_id)
  const [askDirect, setAskDirect] = useState(false)
  const [busy, setBusy] = useState(false)
  useRingtone()

  const accept = async (allowDirect: boolean) => {
    unlockTones()
    if (busy) return
    setBusy(true)
    try {
      if (!(await callManager.ensureSupported())) return
      const { me, config } = useAppStore.getState()
      let relayOnly = relayRequired(me?.settings.calls_p2p, await contactsRepo.has(call.peer_id))
      if (relayOnly && !config?.features?.['calls_relay']) {
        if (!allowDirect) {
          setAskDirect(true)
          return
        }
        relayOnly = false
      }
      await callManager.accept(call, relayOnly)
    } finally {
      setBusy(false)
    }
  }

  const decline = () => {
    setBusy(true)
    void callManager.decline(call).finally(() => setBusy(false))
  }

  return createPortal(
    <div ref={layerRef} data-modal-layer>
      <div
        ref={ref}
        className={styles['screen']}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="incoming-call-title"
        aria-describedby="incoming-call-note"
        tabIndex={-1}
        data-incoming-call={call.id}
      >
        <div className={styles['center']}>
          <div className={cx(styles['avatarWrap'], styles['pulsing'])}>
            <Avatar
              className={styles['avatar']}
              name={peer.name}
              photo={peer.photo}
              availability={peer.availability}
              size="2xl"
            />
          </div>
          <h1 id="incoming-call-title" className={styles['name']}>
            {peer.name}
          </h1>
          <p className={styles['status']} role="status">
            Входящий звонок «Движ»
          </p>
          <span id="incoming-call-note" className={styles['lockNote']}>
            <Icon name="lock" size={14} />
            Сквозное шифрование
          </span>
          {askDirect ? (
            <p className={styles['pill']} role="note">
              Сервер-посредник недоступен: при ответе собеседник увидит ваш IP-адрес.
            </p>
          ) : null}
        </div>

        <div className={styles['controls']}>
          <div className={styles['controlSlot']}>
            <button
              type="button"
              className={cx(styles['control'], styles['danger'])}
              aria-label="Отклонить звонок"
              disabled={busy}
              onClick={decline}
            >
              <Icon name="phone" size={28} className={styles['hangupIcon']} />
            </button>
            Отклонить
          </div>
          <div className={styles['controlSlot']}>
            <button
              type="button"
              className={cx(styles['control'], styles['success'])}
              aria-label={askDirect ? 'Ответить напрямую' : 'Ответить на звонок'}
              disabled={busy}
              onClick={() => void accept(askDirect)}
            >
              <Icon name="phone" size={28} />
            </button>
            {askDirect ? 'Ответить напрямую' : 'Ответить'}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
